import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import type { SettingsResponse } from "../../shared/types";
import { SeasonCard } from "../components/season/Card";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import type { SeasonLayout } from "../components/season/layout";
import { GroupHead } from "../components/season/PageHead";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { formatAt } from "../lib/zones";
import { useSending } from "../sending/SendingContext";
import { SendingStatusPanel } from "../sending/SendingStatusPanel";
import { ownExample, useOpens, useOwnDomain, useOwnDomainSwitch, useSaveAddress, useSlugCheck } from "../setup/AddressStep";
import { HOSTS } from "../setup/hosts";
import { type LookMode, type RecState, shownHost, useCopy } from "../setup/parts";
import { BrowserFrame, PreviewFit, SignupPreview } from "../setup/Previews";
import { recordName, rootOf, trackProblem } from "../setup/rules";
import { BusinessRow, Callout, LookTiles, PasteRows, PasteSheets, PreviewSwitch, RecMark, SeasonRecCard, StField, StJoin, usePaste } from "../setup/SeasonParts";
import { type TrackPart, trackParts } from "../setup/trackParts";
import { EVENTS } from "../setup/TrackingStep";
import { useAuth } from "../auth/AuthContext";
import { useBrandDraft } from "../setup/useBrandDraft";
import type { Run } from "../setup/useSettingsData";
import { useSenderForm } from "../setup/useSenderForm";
import { flipTrack, useTrackWork } from "../setup/useTracking";
import { BOUNCE_LIMIT, HEALTH_WORD, nothingSent, pct, SPAM_LIMIT } from "./SendingPane";
import { ingestSnippet, keyLine, useApiKeys } from "./panes";
import type { PreviewWho } from "../setup/Previews";
import { useAutomationsDraft } from "./useAutomationsDraft";

/** What a pane tells the page that frames it: whether there is something unsaved, and how to save or drop it. */
export interface Frame {
  dirty: boolean;
  saving: boolean;
  save: () => void;
  discard: () => void;
}

export interface PaneProps {
  layout: SeasonLayout;
  data: SettingsResponse;
  run: Run;
  setData: (d: SettingsResponse) => void;
  /** Hands the page this pane's unsaved state. */
  frame: (f: Frame) => void;
}

/** Tells the page framing this pane what is unsaved; the page keeps the latest save and discard to call. */
export function useFrame(report: PaneProps["frame"], f: Frame): void {
  useEffect(() => report(f));
}

const LETTER: Record<TrackPart["kind"], string> = { meta: "M", ga: "G", own: "" };

// --- Brand ---

export type BrandState = ReturnType<typeof useBrandDraft>;

export function BrandPane({ layout, brand, who, data, frame }: { layout: SeasonLayout; brand: BrandState; who: PreviewWho; data: SettingsResponse; frame: PaneProps["frame"] }) {
  const { draft } = brand;
  useFrame(frame, { dirty: brand.dirty, saving: brand.saving, save: () => void brand.save(), discard: brand.discard });
  return (
    <>
      <BusinessRow business={who.business} logoUrl={data.brand.logo_url} />
      {layout === "side" ? null : (
        <>
          <GroupHead title="Preview" />
          <SeasonCard className="st-pvcard">
            <PreviewSwitch colours={draft.colours} who={who} />
          </SeasonCard>
        </>
      )}
      <GroupHead title="Your look" />
      <LookTiles mode={draft.mode as LookMode} colours={draft.colours} onPick={brand.pick} />
      <GroupHead title="Email identity" />
      <div className="k-form">
        <StField label="From name" htmlFor="st-from">
          <input dir="auto" id="st-from" className="inp" value={draft.fromName} onChange={(e) => brand.setFromName(e.target.value)} />
        </StField>
        <StField label="Email footer" htmlFor="st-footer" hint="An unsubscribe link is added after it.">
          <textarea dir="auto" id="st-footer" className="inp" rows={3} value={draft.footer} onChange={(e) => brand.setFooter(e.target.value)} />
        </StField>
      </div>
    </>
  );
}

export function BrandDetail({ brand, who }: { brand: BrandState; who: PreviewWho }) {
  return (
    <>
      <GroupHead title="Preview" />
      <SeasonCard className="st-pvcard">
        <PreviewSwitch colours={brand.draft.colours} who={who} />
      </SeasonCard>
    </>
  );
}

// --- Sending email ---

const checkedAt = (s: SettingsResponse["sending"]) => (s.checked_at ? formatAt(s.checked_at * 1000, { weekday: "short", hour: "numeric", minute: "2-digit" }) : null);

/** Spam, bounces and delivery as three label and value rows, each with the line it pauses at. */
export function HealthRows({ s }: { s: SettingsResponse["sending"] }) {
  const live = s.status === "verified" && !nothingSent(s);
  const row = (label: string, limit: string | null, value: string, tone: "good" | "bad", word: string) => (
    <li className="k-lr" key={label}>
      <div className="k-lr-b">
        <span className="k-k">
          {label}
          {limit ? <small>{limit}</small> : null}
        </span>
        <span className="k-v">
          {value}
          <Pill tone={tone}>{word}</Pill>
        </span>
      </div>
    </li>
  );
  return (
    <>
      <GroupHead
        title="Sending health"
        action={
          <span className="st-gh-r">
            {live ? (
              <Pill tone={s.health_status === "healthy" ? "good" : s.health_status === "paused" ? "bad" : "warn"} dot>
                {HEALTH_WORD[s.health_status] ?? "Healthy"}
              </Pill>
            ) : (
              <Pill>Nothing sent yet</Pill>
            )}
          </span>
        }
      />
      <ul className="k-gl k-kv st-kv">
        {live ? (
          <>
            {row("Spam complaints", "Pauses above 0.3%", pct(s.spam_rate), s.spam_rate > SPAM_LIMIT ? "bad" : "good", s.spam_rate > SPAM_LIMIT ? "Over" : "Safe")}
            {row("Bounces", "Pauses above 4%", pct(s.bounce_rate), s.bounce_rate > BOUNCE_LIMIT ? "bad" : "good", s.bounce_rate > BOUNCE_LIMIT ? "Over" : "Safe")}
            {row("Delivered", null, pct(s.delivered_rate), "good", "Good")}
          </>
        ) : (
          ["Spam complaints", "Bounces", "Delivered"].map((l) => (
            <li className="k-lr" key={l}>
              <div className="k-lr-b">
                <span className="k-k">{l}</span>
                <span className="k-v">None yet</span>
              </div>
            </li>
          ))
        )}
      </ul>
    </>
  );
}

export function SendingPane({ layout, data, run, frame }: PaneProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { refresh } = useSending();
  const s = data.sending;
  const ok = s.status === "verified";
  const form = useSenderForm(data, run);
  const [saving, setSaving] = useState(false);
  const saveAll = async () => {
    setSaving(true);
    if (await form.saveAll()) toast("Saved");
    setSaving(false);
  };
  useFrame(frame, { dirty: form.dirty, saving, save: () => void saveAll(), discard: form.discard });
  const focused = s.state === "paused" || s.state === "recovering" || s.state === "final" || s.state === "stopped";
  const test = async () => {
    const to = await form.sendTest();
    if (to) toast({ text: `Test sent to ${to}`, icon: "send" });
  };
  return (
    <>
      {focused || s.state === "headsup" ? <SendingStatusPanel s={s} run={run} refreshGlobal={refresh} /> : null}
      {focused ? null : (
        <>
          {ok ? (
            <GroupedList noLead className="st-ident">
              <li className="k-lr">
                <button type="button" className="k-lr-b" onClick={() => navigate("/settings/sending/records")}>
                  <span className="k-ico good">
                    <Icon name="shield" />
                  </span>
                  <span className="k-lr-t">
                    <b className="st-brk">{s.domain}</b>
                    <span>
                      <Pill tone="good">Verified</Pill>
                      All {s.records.length} records found
                    </span>
                  </span>
                  <Icon name="chev" />
                </button>
              </li>
            </GroupedList>
          ) : (
            <Callout tone="warn" icon="info">
              <b>Emails are off.</b>
              <Button variant="p" onClick={() => navigate(s.domain ? "/setup/email/records" : "/setup/email")}>
                {s.domain ? "Finish it" : "Set it up"}
              </Button>
            </Callout>
          )}
          {layout === "side" ? null : <HealthRows s={s} />}
          <GroupHead title="Send from your own address" />
          <div className="k-form">
            <StField label="From name" htmlFor="st-sname">
              <input dir="auto" id="st-sname" className="inp" value={form.fromName} onChange={(e) => form.setFromName(e.target.value)} />
            </StField>
            <StField label="From address" htmlFor="st-slocal">
              <StJoin id="st-slocal" value={form.local} disabled={!s.domain} onChange={form.setLocal} suffix={s.domain ? `@${s.domain}` : "@ your sending address"} />
            </StField>
            <StField label="Replies go to" htmlFor="st-sreply">
              <input id="st-sreply" className="inp" type="email" inputMode="email" value={form.reply} onChange={(e) => form.setReply(e.target.value)} />
            </StField>
            {ok ? (
              <div>
                <Button icon="send" disabled={form.sending} onClick={() => void test()}>
                  Send yourself a test email
                </Button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </>
  );
}

/** The records the sending address was verified with, as cards, and where to paste them. */
export function RecordsPane({ data }: { data: SettingsResponse }) {
  const s = data.sending;
  const paste = usePaste(data);
  if (!s.domain || s.status !== "verified") return <Navigate to="/settings/sending" replace />;
  const root = rootOf(s.domain);
  const checked = checkedAt(s);
  return (
    <>
      <p className="st-lead">
        <b>{s.domain}</b>
        <Pill tone="good">All {s.records.length} found</Pill>
      </p>
      {checked ? <p className="k-sub st-chk0">Checked {checked}</p> : null}
      <div className="st-recs">
        {s.records.map((r) => (
          <SeasonRecCard key={`${r.type} ${r.name}`} type={r.type} name={recordName(r.name, root)} value={r.value} state="found" />
        ))}
      </div>
      <PasteRows hostLabel={HOSTS[paste.host].label} onHosts={() => paste.setHosts(true)} onSend={() => paste.setSteps(true)} sentTo={paste.sentTo} />
      <PasteSheets paste={paste} data={data} root={root} />
    </>
  );
}

// --- Web address ---

export function SlugStatus({ avail, slug, suggestion, onUse }: { avail: "ok" | "checking" | "taken" | "bad"; slug: string; suggestion: string | null; onUse: (s: string) => void }) {
  if (avail === "checking") return <RecMark state="checking" />;
  if (avail === "taken") {
    return (
      <span className="st-bad">
        <Icon name="x" /> {slug} is taken.
        {suggestion ? (
          <>
            {" "}
            Try{" "}
            <button type="button" className="k-link st-inl" onClick={() => onUse(suggestion)}>
              {suggestion}
            </button>
          </>
        ) : null}
      </span>
    );
  }
  if (avail === "bad") {
    return (
      <span className="st-bad">
        <Icon name="x" /> Use letters, numbers and dashes
      </span>
    );
  }
  return <RecMark state="found" found="Available, it’s yours" />;
}

function EditAddress({ open, onClose, current }: { open: boolean; onClose: () => void; current: string }) {
  const check = useSlugCheck(current);
  const save = useSaveAddress("settings/domains");
  const changed = check.slug !== current;
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Your free address"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" disabled={check.avail !== "ok" || !changed} onClick={() => void save(check.slug)}>
            Save
          </Button>
        </>
      }
    >
      <div className="st-sh st-sheet-gap">
        <StField label="Your free address" htmlFor="st-slug">
          <StJoin id="st-slug" value={check.slug} onChange={(v) => check.setSlug(v.toLowerCase())} suffix=".flashsuite.net" />
        </StField>
        <div aria-live="polite">{changed ? <SlugStatus avail={check.avail} slug={check.slug} suggestion={check.suggestion} onUse={check.setSlug} /> : null}</div>
      </div>
    </Sheet>
  );
}

/** The address she owns, the one record to add for it, and Verify. */
export function OwnAddress({ data, run, freeHost }: { data: SettingsResponse; run: Run; freeHost: string }) {
  const own = useOwnDomain(data, run);
  const email = useAuth().user?.email;
  return (
    <div className="st-own">
      <StField label="Your own address" htmlFor="st-own">
        <input dir="auto" id="st-own" className="inp" value={own.input} placeholder={ownExample(data, email)} spellCheck={false} autoCapitalize="none" inputMode="url" onChange={(e) => own.setInput(e.target.value)} />
      </StField>
      <p className="st-add">Add this record to {own.root}</p>
      <SeasonRecCard type="CNAME" name={own.name} value={own.cname} state={own.state as RecState} found="Connected" />
      <div className="st-verify">
        {own.state === "found" ? (
          <span className="st-note">Connected. {freeHost} is off.</span>
        ) : (
          <>
            <Button icon="refresh" disabled={own.checking || !own.typed} onClick={() => void own.verify()}>
              Verify
            </Button>
            <span className="st-note">When it connects, {freeHost} turns off.</span>
          </>
        )}
      </div>
    </div>
  );
}

export function OpensRows({ data, run }: { data: SettingsResponse; run: Run }) {
  const o = useOpens(data, run);
  return (
    <GroupedList noLead>
      <li className="k-lr">
        <button type="button" className="k-lr-b st-radio" role="radio" aria-checked={!o.picking} onClick={o.chooseNewest}>
          <i />
          <span className="k-lr-t">
            <b>Your newest room</b>
            <span>{o.published[0] ? `Now ${o.published[0].title}` : "None yet"}</span>
          </span>
        </button>
      </li>
      {o.published.length ? (
        <li className="k-lr">
          <button type="button" className="k-lr-b st-radio" role="radio" aria-checked={o.picking} onClick={() => o.setPicking(true)}>
            <i />
            <span className="k-lr-t">
              <b>A room you pick</b>
            </span>
          </button>
          {o.picking ? (
            <div className="st-pick">
              <select className="inp" aria-label="The room your address opens" value={o.chosen?.id ?? ""} onChange={(e) => o.pick(e.target.value || null)}>
                <option value="">Pick a room</option>
                {o.published.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </li>
      ) : null}
    </GroupedList>
  );
}

/** The switch for an address she owns, and under it the record to add and Verify; turning it off asks first. */
export function OwnAddressRows({ data, run, label, freeHost }: { data: SettingsResponse; run: Run; label: string; freeHost?: string }) {
  const d = data.domain;
  const free = freeHost ?? d.subdomain_host;
  const sw = useOwnDomainSwitch(data, run, free);
  const email = useAuth().user?.email;
  return (
    <>
      <ul className="k-gl k-nolead st-gap">
        <li className="k-set">
          <span className="k-set-t">
            <b>{label}</b>
            <span>Like {ownExample(data, email)}</span>
          </span>
          <Toggle on={sw.open} onChange={sw.flip} label={label} />
        </li>
      </ul>
      {sw.open ? <OwnAddress data={data} run={run} freeHost={free} /> : null}
      <Sheet
        open={sw.asking}
        onClose={() => sw.setAsking(false)}
        title={`Stop using ${d.custom_domain ?? "it"}?`}
        footer={
          <>
            <Button onClick={() => sw.setAsking(false)}>Cancel</Button>
            <Button variant="p" onClick={() => void sw.remove()}>
              Stop using it
            </Button>
          </>
        }
      >
        <p>
          {free} turns back on, and links on {d.custom_domain} stop working.
        </p>
      </Sheet>
    </>
  );
}

export function DomainsPane({ data, run }: PaneProps) {
  const copy = useCopy();
  const d = data.domain;
  const [editing, setEditing] = useState(false);
  const ownLive = d.custom_domain_status === "verified" && !!d.custom_domain;
  return (
    <>
      <GroupHead title="Your free address" />
      <SeasonCard className="st-addr">
        <div className="st-addr-t">
          <span className="k-ico good">
            <Icon name="globe" />
          </span>
          <span className="st-addr-w">
            <b>{d.subdomain_host}</b>
            {ownLive ? <Pill>Turned off</Pill> : <Pill tone="good" dot>Active</Pill>}
            {ownLive ? <span className="st-hint">Replaced by {d.custom_domain}</span> : null}
          </span>
        </div>
        <div className="st-acts">
          <Button icon="copy" onClick={() => copy(d.subdomain_host)}>
            Copy
          </Button>
          <Button icon="pencil" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </div>
      </SeasonCard>
      <OwnAddressRows data={data} run={run} label="Use an address you own" />
      <GroupHead title="Your address opens" />
      <OpensRows data={data} run={run} />
      {editing ? <EditAddress open onClose={() => setEditing(false)} current={d.subdomain} /> : null}
    </>
  );
}

/** The sign-up page in a browser frame, at the address it opens on. */
export function DomainsDetail({ data, who }: { data: SettingsResponse; who: PreviewWho }) {
  const shown = shownHost(data.domain);
  return (
    <>
      <GroupHead title="Your address opens" />
      <PreviewFit>
        <BrowserFrame url={shown}>
          <SignupPreview colours={data.brand} who={{ ...who, address: shown }} />
        </BrowserFrame>
      </PreviewFit>
    </>
  );
}

// --- Tracking ---

type OwnRow = { kind: "own"; name: string; on: false; state: "off"; value: null; id?: undefined };

function TrackStatus({ part }: { part: TrackPart | OwnRow }) {
  if (part.state === "loaded") return <Pill tone="good" dot>Loaded</Pill>;
  if (part.state === "bad") return <Pill tone="warn" dot>Needs a valid ID</Pill>;
  if (part.state === "waiting") return <Pill dot>Waiting for a visit</Pill>;
  return <Pill tone="line">Off</Pill>;
}

function TrackSheet({ kind, data, run, setData, onClose }: { kind: TrackPart["kind"]; data: SettingsResponse; run: Run; setData: PaneProps["setData"]; onClose: () => void }) {
  const toast = useToast();
  const { cards, patch, saveOne, checkLoads, tokenSaved } = useTrackWork(data, run, setData, kind);
  const c = cards[kind];
  const own = kind === "own";
  const problem = trackProblem(kind, c.value);
  const meta = {
    meta: { name: "Meta pixel", label: "Pixel ID", ph: "15 or 16 digits", hint: "In Meta Events Manager, under Data sources." },
    ga: { name: "Google Analytics", label: "Measurement ID", ph: "G-XXXXXXXXXX", hint: "In Google Analytics, under Admin, then Data streams." },
    own: { name: "Your own script", label: "Paste the code your tool gave you", ph: "<script>…</script>", hint: "" },
  }[kind];
  const save = async () => {
    if (problem) return patch(kind, { st: "bad" });
    if ((await saveOne(kind, c)) === false) return;
    toast(`${meta.name} saved`);
    onClose();
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={meta.name}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" disabled={c.st === "checking"} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="st-sh st-sheet-gap">
        <StField label={meta.label} htmlFor="st-tid" hint={meta.hint}>
          {own ? (
            <textarea dir="auto" id="st-tid" className="inp" rows={4} placeholder={meta.ph} spellCheck={false} value={c.value} onChange={(e) => patch(kind, { value: e.target.value, st: "none" })} />
          ) : (
            <input dir="auto" id="st-tid" className="inp" placeholder={meta.ph} inputMode={kind === "meta" ? "numeric" : "text"} autoComplete="off" spellCheck={false} value={c.value} onChange={(e) => patch(kind, { value: e.target.value, st: "none" })} />
          )}
        </StField>
        <p className="st-need">{problem}</p>
        {kind === "meta" ? (
          <StField label={<>Conversions API token <span className="st-opt">(optional)</span></>} htmlFor="st-ttok">
            <input id="st-ttok" className="inp" type="password" autoComplete="off" placeholder={tokenSaved ? "Saved. Paste a new one to replace it" : "Paste the token"} value={c.token} onChange={(e) => patch(kind, { token: e.target.value })} />
          </StField>
        ) : null}
        <div className="st-chk">
          <Button icon="refresh" disabled={c.st === "checking" || !!problem} onClick={() => void checkLoads(kind)}>
            Check it loads
          </Button>
          <span aria-live="polite">
            {c.st === "loaded" ? <RecMark state="found" found="Loaded on your pages" /> : null}
            {c.st === "checking" ? (
              <span className="st-rst plain">
                <span className="st-spin" />
                Opening your sign-up page
              </span>
            ) : null}
            {c.st === "waiting" ? (
              <span className="st-rst plain">
                <span className="st-o" />
                Waiting for a visit
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </Sheet>
  );
}

export function ScriptsPane({ data, run, setData }: PaneProps) {
  const [open, setOpen] = useState<TrackPart["kind"] | null>(null);
  const parts: Array<TrackPart | OwnRow> = trackParts(data);
  if (!parts.some((p) => p.kind === "own")) parts.push({ kind: "own", name: "Your own script", on: false, state: "off", value: null });
  const refusal = data.measurement.meta_pixel_id && data.measurement.checks.server.state === "bad" ? data.measurement.checks.server.refusal : null;
  return (
    <>
      <GroupedList>
        {parts.map((p) => (
          <li key={p.id ?? p.kind} className="k-lr st-trk">
            <button type="button" className="st-trk-b" onClick={() => setOpen(p.kind)}>
              <span className="k-ico">{LETTER[p.kind] ? <b>{LETTER[p.kind]}</b> : <Icon name="code" />}</span>
              <span className="k-lr-t">
                <b>{p.name}</b>
                {p.value && p.kind !== "own" ? <span className="st-brk">{p.value}</span> : null}
                <span>
                  <TrackStatus part={p} />
                </span>
              </span>
            </button>
            <Toggle
              on={p.on}
              label={`${p.name} on or off`}
              onChange={() => {
                if (!flipTrack(p as TrackPart, run)) setOpen(p.kind);
              }}
            />
          </li>
        ))}
      </GroupedList>
      {refusal ? (
        <div className="st-gap">
          <Callout tone="warn" icon="info">
            <b>Meta turned an event away.</b> {refusal}
          </Callout>
        </div>
      ) : null}
      {open ? <TrackSheet kind={open} data={data} run={run} setData={setData} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** What each tool is told, moment by moment, by the tools that are on. */
export function ScriptsDetail({ data }: { data: SettingsResponse }) {
  const parts = trackParts(data);
  const meta = parts.some((p) => p.kind === "meta" && p.on);
  const ga = parts.some((p) => p.kind === "ga" && p.on);
  return (
    <>
      <GroupHead title="What your tools hear" />
      <GroupedList noLead>
        {EVENTS.map(([icon, title, metaName, gaName]) => (
          <ListRow
            key={title}
            icon={icon}
            title={title}
            sub={
              <span className="st-chips">
                {meta ? <span className="tag">{metaName}</span> : null}
                {ga && gaName ? <span className="tag">{gaName}</span> : null}
                {!meta && !(ga && gaName) ? <span className="tag">Nothing yet</span> : null}
              </span>
            }
          />
        ))}
      </GroupedList>
    </>
  );
}

// --- Automations ---

export function AutomationsPane({ data, run, frame }: PaneProps) {
  const a = useAutomationsDraft(data, run);
  useFrame(frame, { dirty: a.dirty, saving: a.saving, save: () => void a.save(), discard: a.discard });
  return (
    <>
      <GroupHead title="Stopped watching after" />
      <SeasonCard className="st-mins">
        <div className="st-mins-r">
          <input className="inp" type="number" min={1} max={1440} inputMode="numeric" value={a.mins} aria-label="Stopped watching after, in minutes" onChange={(e) => a.setMins(e.target.value)} />
          <span>minutes</span>
        </div>
        <p className="st-sub">Then the tag stopped-watching goes on</p>
      </SeasonCard>
      <GroupHead title="Tell the team" />
      <p className="st-sub st-sub0">Hear about replies, messages and answers</p>
      <GroupedList noLead className="st-team">
        {a.team.map((e) => (
          <li key={e} className="k-lr">
            <div className="k-lr-b">
              <span className="k-lr-t">
                <b>{e}</b>
              </span>
              <button type="button" className="k-ib" aria-label={`Remove ${e}`} onClick={() => a.remove(e)}>
                <Icon name="x" />
              </button>
            </div>
          </li>
        ))}
      </GroupedList>
      <div className="st-add-r">
        <input
          className="inp"
          type="email"
          inputMode="email"
          placeholder="Add an email, then Enter"
          aria-label="Add an email"
          autoComplete="off"
          autoCapitalize="none"
          value={a.adding}
          onChange={(e) => a.setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") a.add();
          }}
        />
        <Button icon="plus" onClick={a.add}>
          Add
        </Button>
      </div>
    </>
  );
}

// --- API keys ---

function KeyRow({ value, shown, what }: { value: string; shown: string; what: string }) {
  const copy = useCopy();
  return (
    <div className="st-key">
      <span>{shown}</span>
      <Button icon="copy" aria-label={`Copy the ${what}`} onClick={() => copy(value)}>
        Copy
      </Button>
    </div>
  );
}

export function ApiPane() {
  const k = useApiKeys();
  const copy = useCopy();
  const { work, read } = k;
  const key = work?.key ?? null;
  return (
    <>
      <GroupHead
        title="Workspace key"
        action={
          <span className="st-gh-r">
            {work?.prefix ? <Pill tone="good" dot>Active</Pill> : <Pill>No key yet</Pill>}
          </span>
        }
      />
      <SeasonCard>
        {!work?.prefix ? (
          <Button variant="p" icon="plus" disabled={k.busy} onClick={() => void k.issue("full")}>
            Create key
          </Button>
        ) : (
          <>
            {key ? (
              <KeyRow value={key} shown={`${key.slice(0, 9)}••••••••${key.slice(-4)}`} what="key" />
            ) : (
              <div className="st-key">
                <span className="muted">{work.prefix}••••••••</span>
              </div>
            )}
            <p className="st-sub">{keyLine(work)}</p>
            <div className="st-acts one">
              <Button icon="refresh" disabled={k.busy} onClick={() => k.setAsking(true)}>
                {key ? "Replace" : "Make a copyable key"}
              </Button>
            </div>
          </>
        )}
      </SeasonCard>
      <GroupHead title="Read-only key" />
      <SeasonCard>
        {read?.key ? (
          <KeyRow value={read.key} shown={read.key} what="read-only key" />
        ) : (
          <Button icon="plus" disabled={k.busy} onClick={() => void k.issue("read")}>
            Create read-only key
          </Button>
        )}
      </SeasonCard>
      <GroupHead title="How to send" />
      <GroupedList noLead>
        <li className="k-lr">
          <div className="k-lr-b">
            <span className="k-lr-t">
              <b>flashroom.flashsuite.ai</b>
              <span>/api/ingest</span>
            </span>
            <Button icon="copy" onClick={() => copy(ingestSnippet(k.shown && key ? key : "frk_live_…"), "Copied")}>
              Copy
            </Button>
          </div>
        </li>
      </GroupedList>
      <Sheet
        open={k.asking}
        onClose={() => k.setAsking(false)}
        title="Replace the key?"
        footer={
          <>
            <Button onClick={() => k.setAsking(false)}>Cancel</Button>
            <Button variant="p" disabled={k.busy} onClick={() => void k.issue("full")}>
              Replace it
            </Button>
          </>
        }
      >
        <p>Replacing makes a new key. The old one stops working at once, so update your product first.</p>
      </Sheet>
    </>
  );
}
